"use client"

import { useEffect, useState } from "react"

import { createPortal } from "react-dom"

interface PageVeilProps {
  onDismiss: () => void
  label: string
  className?: string
}

// WebKit clips a fixed element to the visual viewport while iOS keeps painting the page below it,
// behind Safari's bottom bar, so a fixed veil leaves that strip sharp. The bleed absorbs the
// keyboard pan, which moves the visual viewport without moving the document.
const BLEED = 400

// The header is a flex item with a z-index, so it stacks as a unit and the dock rides inside it.
// A veil outside that unit cannot cover the bar; one inside it sits under the dock on source order.
const findShell = () => Array.from(document.querySelectorAll<HTMLElement>("[data-app-shell]")).at(-1)
const findHost = () => findShell()?.querySelector<HTMLElement>(":scope > header") ?? document.body

const frameOf = () => ({
  top: Math.round(-(findShell() ?? document.body).getBoundingClientRect().top) - BLEED,
  height: window.innerHeight + BLEED * 2,
})

export const PageVeil = ({ onDismiss, label, className }: PageVeilProps) => {
  const [host, setHost] = useState<HTMLElement>()
  const [frame, setFrame] = useState<ReturnType<typeof frameOf>>()

  useEffect(() => {
    setHost(findHost())
    let pending = 0
    const measure = () => {
      pending = 0
      setFrame(frameOf())
    }
    const schedule = () => {
      if (pending === 0) pending = requestAnimationFrame(measure)
    }
    measure()
    const viewport = window.visualViewport
    viewport?.addEventListener("resize", schedule)
    window.addEventListener("scroll", schedule, { passive: true })
    window.addEventListener("resize", schedule)
    window.addEventListener("orientationchange", schedule)
    return () => {
      if (pending !== 0) cancelAnimationFrame(pending)
      viewport?.removeEventListener("resize", schedule)
      window.removeEventListener("scroll", schedule)
      window.removeEventListener("resize", schedule)
      window.removeEventListener("orientationchange", schedule)
    }
  }, [])

  if (!host || !frame) return null

  return createPortal(
    <button
      type="button"
      aria-label={label}
      onClick={onDismiss}
      style={frame}
      className={`bg-scrim motion-safe:animate-fade-in z-dropdown absolute inset-x-0 cursor-default backdrop-blur-md ${className ?? ""}`}
    />,
    host
  )
}
